/*
 * Resolve a media path returned by the API into something an <img> or a CSS
 * url() can load.
 *
 * The API is inconsistent about this and cannot be relied on either way:
 * `/api/school/paths/` returns absolute URLs built by the serializer, while the
 * learned-school, learned-skill and character-item serializers return the raw
 * ImageField path (`/media/icons/...`). A relative path works only when the
 * frontend is served from the same origin as the API, which it is not in
 * production — nginx serves the SPA and Django serves /media separately.
 *
 * Absolute URLs and data: URIs pass through untouched, so this is safe to apply
 * to every icon regardless of which serializer produced it.
 */

const ORIGIN = () => String(import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

/*
 * An absolute /media/ URL carries whatever host:port served the request that
 * built it — Django's `request.build_absolute_uri`. Once such a URL is stored
 * (a client-side cache, a fixture, a dump taken from another environment) it
 * outlives that host: a GM background minted when the backend answered on :8001
 * still points at :8001 today, and loads nothing.
 *
 * So the origin of a media URL is never trusted — only its path is. Absolute
 * URLs that are not media (external art, CDNs) pass through untouched.
 */
function rebaseMedia(value) {
    const origin = ORIGIN();
    if (!origin) return value;
    try {
        const parsed = new URL(value, window.location.origin);
        if (!/^\/media\//i.test(parsed.pathname)) return value;
        return `${origin}${parsed.pathname}${parsed.search}`;
    } catch {
        return value;
    }
}

export function mediaUrl(path) {
    if (typeof path !== 'string') return '';
    const value = path.trim();
    if (!value) return '';
    if (value.startsWith('data:')) return value;
    if (/^(https?:)?\/\//i.test(value)) return rebaseMedia(value);
    return `${ORIGIN()}/${value.replace(/^\/+/, '')}`;
}

/*
 * The same value as a CSS background-image declaration.
 *
 * The URL is API data going into a CSS grammar, so its delimiters are
 * percent-encoded before interpolation — a raw `")` would otherwise close the
 * url() and inject declarations. Same rule as the campaign card art.
 */
export function backgroundImageOf(path) {
    const url = mediaUrl(path);
    if (!url) return null;
    const safe = url.replace(/["'()\\\s]/g, encodeURIComponent);
    return `url("${safe}")`;
}
